/* @jsx h */
import { defineComponent, ref, h, computed } from "vue";
import { useTrpc } from "@app/vue/providers/index";
import { useToast } from "@app/vue/providers/Toast";

export const GoogleReviews = defineComponent({
  name: "GoogleReviews",
  setup() {
    const trpc = useTrpc();
    const toast = useToast();

    const placeId = ref<string | null>(null);
    const rating = ref<number | null>(null);
    const reviewCount = ref<number | null>(null);
    const fetchedAt = ref<string | null>(null);

    const searchQuery = ref("");
    const searchResults = ref<
      Array<{ place_id: string; name: string; address: string }>
    >([]);
    const isSearching = ref(false);
    const isRefreshing = ref(false);
    const isSaving = ref(false);
    const isLoading = ref(true);
    const showSearch = ref(false);

    // Load current data
    const loadData = async () => {
      try {
        isLoading.value = true;
        const data = await trpc.googleReviews.get.query();
        placeId.value = data.google_place_id;
        rating.value = data.google_rating;
        reviewCount.value = data.google_review_count;
        fetchedAt.value = data.google_rating_fetched_at;
      } catch (error) {
        console.error("Failed to load Google review data:", error);
      } finally {
        isLoading.value = false;
      }
    };

    loadData();

    const handleSearch = async () => {
      if (searchQuery.value.length < 2) return;
      isSearching.value = true;
      try {
        const results = await trpc.googleReviews.searchPlace.mutate({
          query: searchQuery.value,
        });
        searchResults.value = results;
      } catch (error) {
        console.error("Search failed:", error);
        toast.showToast("Search failed. Please try again.", { type: "error" });
      } finally {
        isSearching.value = false;
      }
    };

    const handleSelectPlace = async (selectedPlaceId: string) => {
      isSaving.value = true;
      try {
        await trpc.googleReviews.updatePlaceId.mutate({
          google_place_id: selectedPlaceId,
        });
        placeId.value = selectedPlaceId;
        rating.value = null;
        reviewCount.value = null;
        fetchedAt.value = null;
        searchResults.value = [];
        searchQuery.value = "";
        showSearch.value = false;
        toast.showToast("Google Place ID saved.", { type: "success" });

        // Auto-refresh rating after setting place ID
        await handleRefresh();
      } catch (error) {
        console.error("Failed to save Place ID:", error);
        toast.showToast("Failed to save. Please try again.", { type: "error" });
      } finally {
        isSaving.value = false;
      }
    };

    const handleRefresh = async () => {
      isRefreshing.value = true;
      try {
        const result = await trpc.googleReviews.refreshRating.mutate({
          force: true,
        });
        rating.value = result.google_rating;
        reviewCount.value = result.google_review_count;
        fetchedAt.value = result.google_rating_fetched_at;
        if (!result.cached) {
          toast.showToast("Rating refreshed from Google.", { type: "success" });
        }
      } catch (error: any) {
        console.error("Failed to refresh rating:", error);
        toast.showToast(error?.message || "Failed to refresh rating.", {
          type: "error",
        });
      } finally {
        isRefreshing.value = false;
      }
    };

    const handleDisconnect = async () => {
      isSaving.value = true;
      try {
        await trpc.googleReviews.updatePlaceId.mutate({
          google_place_id: null,
        });
        placeId.value = null;
        rating.value = null;
        reviewCount.value = null;
        fetchedAt.value = null;
        toast.showToast("Google Reviews disconnected.", { type: "success" });
      } catch (error) {
        console.error("Failed to disconnect:", error);
      } finally {
        isSaving.value = false;
      }
    };

    const formattedFetchedAt = computed(() => {
      if (!fetchedAt.value) return null;
      const date = new Date(fetchedAt.value);
      return date.toLocaleString();
    });

    const renderStars = (value: number) => {
      const stars = [];
      const fullStars = Math.floor(value);
      const hasHalf = value - fullStars >= 0.3;
      const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);

      for (let i = 0; i < fullStars; i++) {
        stars.push(
          <span class="text-yellow-400 text-lg" key={`full-${i}`}>
            ★
          </span>,
        );
      }
      if (hasHalf) {
        stars.push(
          <span class="text-yellow-400 text-lg opacity-50" key="half">
            ★
          </span>,
        );
      }
      for (let i = 0; i < emptyStars; i++) {
        stars.push(
          <span class=" text-lg" key={`empty-${i}`}>
            ★
          </span>,
        );
      }
      return stars;
    };

    return () => (
      <div class="space-y-4">
        {isLoading.value ? (
          <div class="flex items-center justify-center py-4">
            <span class="loading loading-spinner loading-sm"></span>
          </div>
        ) : placeId.value ? (
          // Connected state — show rating + controls
          <div class="space-y-4">
            {/* Current rating display */}
            <div class="bg-base-200 rounded-lg p-4">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                    <svg
                      viewBox="0 0 24 24"
                      width="24"
                      height="24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                  </div>
                  <div>
                    {rating.value !== null ? (
                      <div class="flex items-center gap-2">
                        <span class="text-2xl font-bold text-accent">
                          {rating.value.toFixed(1)}
                        </span>
                        <div class="flex">{renderStars(rating.value)}</div>
                        <span class="text-sm text-base-content/60">
                          ({reviewCount.value?.toLocaleString()} reviews)
                        </span>
                      </div>
                    ) : (
                      <span class="text-sm text-base-content/60">
                        No rating data yet. Click refresh to fetch.
                      </span>
                    )}
                    {formattedFetchedAt.value && (
                      <p class="text-xs text-base-content/40 mt-0.5">
                        Last updated: {formattedFetchedAt.value}
                      </p>
                    )}
                  </div>
                </div>
                <div class="flex gap-2">
                  <button
                    type="button"
                    class="btn btn-ghost btn-sm"
                    disabled={isRefreshing.value}
                    onClick={handleRefresh}
                  >
                    {isRefreshing.value ? (
                      <span class="loading loading-spinner loading-xs"></span>
                    ) : (
                      <span class="material-symbols-outlined text-lg">
                        refresh
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Place ID info */}
            <div class="flex items-center justify-between">
              <div>
                <p class="text-xs text-base-content/50">
                  Place ID: {placeId.value}
                </p>
              </div>
              <button
                type="button"
                class="btn btn-ghost btn-xs text-error"
                onClick={handleDisconnect}
                disabled={isSaving.value}
              >
                Disconnect
              </button>
            </div>

            <p class="text-xs text-base-content/40">
              Your Google rating is displayed on your customer booking page.
              Rating data is cached and refreshed automatically every 12 hours.
            </p>
          </div>
        ) : (
          // Not connected — show search
          <div class="space-y-4">
            <p class="text-sm text-base-content/70">
              Connect your Google Business listing to display your review score
              on the customer booking page.
            </p>

            {!showSearch.value ? (
              <button
                type="button"
                class="btn btn-primary btn-sm gap-2"
                onClick={() => (showSearch.value = true)}
              >
                <span class="material-symbols-outlined text-lg">search</span>
                Find your restaurant on Google
              </button>
            ) : (
              <div class="space-y-3">
                <div class="flex gap-2">
                  <input
                    type="text"
                    class="input input-bordered input-sm flex-1"
                    placeholder="Search your restaurant name..."
                    value={searchQuery.value}
                    onInput={(e: any) => (searchQuery.value = e.target.value)}
                    onKeydown={(e: KeyboardEvent) => {
                      if (e.key === "Enter") handleSearch();
                    }}
                  />
                  <button
                    type="button"
                    class="btn btn-primary btn-sm"
                    disabled={isSearching.value || searchQuery.value.length < 2}
                    onClick={handleSearch}
                  >
                    {isSearching.value ? (
                      <span class="loading loading-spinner loading-xs"></span>
                    ) : (
                      "Search"
                    )}
                  </button>
                </div>

                {/* Search results */}
                {searchResults.value.length > 0 && (
                  <div class="space-y-2">
                    {searchResults.value.map((result) => (
                      <div
                        key={result.place_id}
                        class="bg-base-200 rounded-lg p-3 flex items-center justify-between cursor-pointer hover:bg-base-300 transition-colors"
                        onClick={() => handleSelectPlace(result.place_id)}
                      >
                        <div>
                          <p class="font-medium text-sm text-accent">
                            {result.name}
                          </p>
                          <p class="text-xs text-base-content/50">
                            {result.address}
                          </p>
                        </div>
                        <span class="material-symbols-outlined text-primary text-lg">
                          add_circle
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  class="btn btn-ghost btn-xs"
                  onClick={() => {
                    showSearch.value = false;
                    searchResults.value = [];
                    searchQuery.value = "";
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
});
